// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AssignVetButton — per-row "Assign vet" action for the patient list.
 *
 * Master-only (a vet/vetadmin with no parent). Opens AssignVetModal for the
 * row's pet → POST /api/pets/:id/assign-vet. Quiet text button per the UI
 * style guide (secondary/per-row actions stay low-emphasis).
 *
 * The label reports the row's CURRENT state: "Assign vet" when unassigned,
 * "Reassign" once a vet is responsible. It used to be the constant "Assign
 * vet", so a master had no way to tell an assignment had taken once the
 * success toast faded — the reading behind Hillside Animal Hospital's
 * 2026-08-03 report that "the assign vet link still shows".
 */
import { Button, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useGetIdentity, useRecordContext, useRefresh, useTranslate } from 'react-admin'
import { petAssigneeIds } from '../clinics/clinicAssociation'
import type { Pet } from '../types'
import AssignmentHistoryDialog from './AssignmentHistoryDialog'
import AssignVetModal from './AssignVetModal'
import { useAssigneeNames } from './useAssigneeNames'

// Its own component so the names query (useAssigneeNames) only mounts for a
// master who is looking at an assigned patient — every other PetShow visitor
// pays nothing.
const AssigneeNames: React.FC<{ pet: Pet }> = ({ pet }) => <>{useAssigneeNames(pet).join(', ')}</>

interface AssignVetButtonProps {
  /** PetShow header (vic/vetpo#68): outlined like its neighbours, names every
   *  responsible vet beside the button (#70) and adds the History dialog. The list row keeps the quiet
   *  text button — the Responsible Vet column already shows the name there. */
  header?: boolean
}

const AssignVetButton: React.FC<AssignVetButtonProps> = ({ header = false }) => {
  const translate = useTranslate()
  const record = useRecordContext<Pet>()
  const { identity } = useGetIdentity()
  const refresh = useRefresh()
  const [open, setOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  // Only a master (vet/vetadmin with no parent) can assign patients.
  const isMaster =
    (identity?.role === 'vet' || identity?.role === 'vetadmin') && !identity?.masterAccount
  if (!record || !isMaster) return null

  const handleClose = (changed: boolean) => {
    setOpen(false)
    if (changed) refresh()
  }

  const assignedVetIds = petAssigneeIds(record)
  const isAssigned = assignedVetIds.length > 0

  return (
    <>
      {header && (
        <Typography variant="body2" color="text.secondary" data-testid="pet-assigned-vet">
          {translate('resources.pets.fields.assigned_vet')}:{' '}
          {isAssigned ? (
            <AssigneeNames pet={record} />
          ) : (
            translate('pos.clinic_team.unassigned_short')
          )}
        </Typography>
      )}
      <Button
        size="small"
        variant={header ? 'outlined' : 'text'}
        data-testid="assign-vet-button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        {translate(
          isAssigned ? 'pos.clinic_team.reassign_action' : 'pos.clinic_team.assign_action',
        )}
      </Button>
      {header && (
        <Button
          size="small"
          variant="outlined"
          data-testid="assignment-history-button"
          onClick={() => setHistoryOpen(true)}
        >
          {translate('pos.clinic_team.history_action')}
        </Button>
      )}
      {historyOpen && (
        <AssignmentHistoryDialog
          open={historyOpen}
          petId={Number(record.id)}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {open && (
        <AssignVetModal
          open={open}
          petId={Number(record.id)}
          petName={record.name || undefined}
          currentAssignedVetIds={assignedVetIds}
          onClose={handleClose}
        />
      )}
    </>
  )
}

export default AssignVetButton
